

import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Link, useMediaQuery, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Import Material-UI icons for amenities
import BedIcon from '@mui/icons-material/Bed';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import ApartmentIcon from '@mui/icons-material/Apartment';
import BathtubIcon from '@mui/icons-material/Bathtub';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PoolIcon from '@mui/icons-material/Pool';
import PetsIcon from '@mui/icons-material/Pets';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import TvIcon from '@mui/icons-material/Tv';
import ChairIcon from '@mui/icons-material/Chair';

// Function to get the appropriate icon based on amenity type
const getAmenityIcon = (amenityType) => {
  // Map amenity types to their corresponding icons
  const amenityIconMap = {
    'bed': <BedIcon sx={{ color: '#626262', fontSize: 20 }} />,
    'area': <SquareFootIcon sx={{ color: '#626262', fontSize: 20 }} />,
    'floor': <ApartmentIcon sx={{ color: '#626262', fontSize: 20 }} />,
    'bath': <BathtubIcon sx={{ color: '#626262', fontSize: 20 }} />,
    'parking': <LocalParkingIcon sx={{ color: '#626262', fontSize: 20 }} />,
    'gym': <FitnessCenterIcon sx={{ color: '#626262', fontSize: 20 }} />,
    'pool': <PoolIcon sx={{ color: '#626262', fontSize: 20 }} />,
    'pets': <PetsIcon sx={{ color: '#626262', fontSize: 20 }} />,
    'ac': <AcUnitIcon sx={{ color: '#626262', fontSize: 20 }} />,
    'tv': <TvIcon sx={{ color: '#626262', fontSize: 20 }} />,
    'sofa': <ChairIcon sx={{ color: '#626262', fontSize: 20 }} />
  };

  // Return the icon or a default icon if not found
  return amenityIconMap[amenityType] || <SquareFootIcon sx={{ color: '#626262', fontSize: 20 }} />;
};

const ResidentialProperties = () => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get user data from Redux store for recording views
  const isLoggedIn = useSelector(state => state.user.isAuthenticated);
  const userData = useSelector(state => state.user.user);

  useEffect(() => {
    const fetchResidentialProperties = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://propertydealstalk.com/api/property/filterProperties?property_type=residential');

        if (!response.ok) {
          throw new Error('Failed to fetch residential properties');
        }

        const data = await response.json();

        if (data.statusCode === 200 && data.result && data.result.properties) {
          setProperties(data.result.properties);
        } else {
          throw new Error(data.message || 'Failed to get property data');
        }
      } catch (err) {
        console.error('Error fetching residential properties:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResidentialProperties();
  }, []);

  // Function to record property view
  const recordPropertyView = async (propertyId) => {
    if (!isLoggedIn || !userData) {
      console.log('User not logged in, skipping view recording');
      return;
    }

    try {
      // Get the user_id from userData
      const userId = userData.id;
      if (!userId) {
        console.error('User ID not found in user data');
        return;
      }

      console.log('Recording property view with user_id:', userId, 'property_id:', propertyId);

      const response = await fetch('https://propertydealstalk.com/api/buyer/recordPropertyView', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          property_id: propertyId
        }),
      });

      const data = await response.json();
      if (data.statusCode === 200) {
        console.log('Property view recorded successfully:', data.result);
      } else {
        console.error('Failed to record property view:', data.message);
      }
    } catch (err) {
      console.error('Error recording property view:', err);
    }
  };

  // ✅ Function to check if image has valid data
  const hasValidImage = (base64) => {
    return base64 && base64.length > 100 && base64 !== "iVBORw0KGgoAAAANSUhEUgAAB4AAAAQ4CAYAAADo08FDAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAP+6SURBVHhe7N13fFRV/sbxz52aNqSHEGoSeleaKApYsGNby651rbv2Xde1rb3gWra46tpiXqHUNAAAAAASUVORK5CYII=";
  };

  // ✅ Function to check if a value is valid (not null, undefined, 'N/A', or empty)
  const isValidValue = (value) => {
    return value && value !== 'N/A' && value !== 'null' && value !== null && value !== undefined && value.toString().trim() !== '';
  };

  // ✅ Format price with commas
  const formatPrice = (price) => {
    if (!price || price === 0 || price === '0') return '';
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // ✅ FIXED: Function to get display price - matches TalkView.jsx logic
  const getDisplayPrice = (property) => {
    // Check if this is a rental property (case-insensitive comparison)
    const category = property.category || property.commercial_catgory || '';
    const isRental = category.toLowerCase() === 'rent';

    if (isRental) {
      // For rental properties, prioritize monthly_rent
      if (isValidValue(property.monthly_rent) && property.monthly_rent !== 0) {
        return property.monthly_rent;
      }
    }

    // For sale properties or if monthly_rent is not available, use asking_price
    if (isValidValue(property.asking_price) && property.asking_price !== 0) {
      return property.asking_price;
    }

    return null; // Return null if no price is available
  };

  // Navigate to the ResidentialView page
  const handleViewAllClick = (e) => {
    e.preventDefault();
    navigate('/residential-view');
  };

  // Handle property card click - navigate to TalkView with property ID
  const handlePropertyClick = (propertyId) => {
    // Record the property view
    recordPropertyView(propertyId);
    // Navigate to property details page
    navigate(`/view/${propertyId}`);
  };

  // Display only 8 properties on the main page
  const displayedProperties = properties.slice(0, 8);

  // ✅ UPDATED: Function to get only valid property amenities
  const getPropertyAmenities = (property) => {
    const amenities = [];

    // Check property size
    if (isValidValue(property.property_size)) {
      const sizeUnit = isValidValue(property.property_size_units) ? property.property_size_units : 'sq.ft';
      amenities.push({
        type: 'area',
        value: `${property.property_size} ${sizeUnit}`
      });
    }

    // Check BHK
    if (isValidValue(property.bhk)) {
      amenities.push({
        type: 'bed',
        value: `${property.bhk} BHK`
      });
    }

    // Check Floor - only add if both select_floor and total_floors are valid
    if (isValidValue(property.select_floor) && isValidValue(property.total_floors)) {
      amenities.push({
        type: 'floor',
        value: `${property.select_floor}/${property.total_floors} Floor`
      });
    }

    return amenities;
  };

  return (
    <Box
      sx={{
        px: '40px',
        pb: '40px',
        fontFamily: 'Outfit',
        bgcolor: '#F9FAFB',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          py: '30px',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 600, m: 0 }}>
            Residential Properties
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#626262', mt: '4px', fontWeight: 400 }}>
            Leading Project in high demand
          </Typography>
        </Box>
        <Link
          href="#"
          underline="none"
          sx={{ fontWeight: 500, color: '#626262' }}
          onClick={handleViewAllClick}
        >
          View All
        </Link>
      </Box>

      {/* Property Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 4, color: 'error.main' }}>
          <Typography>{error}</Typography>
        </Box>
      ) : (
        <Grid
          container
          spacing={2}
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? '1fr'
              : isTablet
              ? 'repeat(2, 1fr)'
              : 'repeat(4, 1fr)',
            gap: '20px',
          }}
        >
          {displayedProperties.map((property, i) => {
            const amenities = getPropertyAmenities(property); // ✅ Get valid amenities
            const hasImage = hasValidImage(property.image);
            const displayPrice = getDisplayPrice(property);
            const isRental = (property.category || property.commercial_catgory || '').toLowerCase() === 'rent';

            return (
              <Box
                key={property.id}
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  height: isMobile ? 'auto' : '200px',
                  bgcolor: '#fff',
                  border: '1px solid #D1D5DB',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                  },
                }}
                onClick={() => handlePropertyClick(property.id)}
              >
                {/* Image */}
                {hasImage ? (
                  <Box
                    component="img"
                    src={`data:image/png;base64,${property.image}`}
                    alt={property.property_name}
                    sx={{
                      width: isMobile ? '100%' : '50%',
                      height: isMobile ? '200px' : '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: isMobile ? '100%' : '50%',
                      height: isMobile ? '200px' : '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5',
                      border: '1px dashed #ccc'
                    }}
                  >
                    <Typography variant="body2" color="#888" textAlign="center" px={2}>
                      No images uploaded
                    </Typography>
                  </Box>
                )}

                {/* Info */}
                <Box
                  sx={{
                    width: isMobile ? '100%' : '50%',
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                  }}
                >
                  {/* Property Name */}
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#000',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mb: 0.5
                    }}
                  >
                    {property.property_name}
                  </Typography>

                  {/* Seller Name */}
                  {property.seller && property.seller.full_name ? (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: '#626262',
                        fontWeight: 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 0.5
                      }}
                    >
                      by {property.seller.full_name}
                    </Typography>
                  ) : (
                    <Box sx={{ height: '16px', mb: 0.5 }} />
                  )}

                  {/* Location */}
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: '#626262',
                      fontWeight: 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mb: 0.5
                    }}
                  >
                    {property.area_name}, {property.location}
                  </Typography>

                  {/* ✅ UPDATED: Icons - Only show valid amenities */}
                  {amenities.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '3px',
                        mb: 0.5
                      }}
                    >
                      {amenities.map((amenity, index) => (
                        <Box
                          key={index}
                          component="span"
                          sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          {getAmenityIcon(amenity.type)}
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: '#626262',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {amenity.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* ✅ FIXED: Price with proper spacing and conditional rendering */}
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#2563EB',
                      fontSize: 15,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%'
                    }}
                  >
                    {displayPrice ? (
                      <>
                        ₹ {formatPrice(displayPrice)}
                        {isRental && '/month'}
                      </>
                    ) : (
                      'Price on Request'
                    )}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default ResidentialProperties;

